import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/lib/supabase";
import { Checkbox } from "@/components/ui/checkbox";
import { Link } from "react-router-dom";

export const SignUpForm = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [username, setUsername] = useState("");
  const [loading, setLoading] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!acceptedTerms) {
      toast({
        variant: "destructive",
        title: "Terms & Conditions Required",
        description: "Please accept the terms and conditions to continue.",
      });
      return;
    }

    setLoading(true);

    try {
      const { data: { user }, error: signUpError } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            username,
          },
        },
      });

      if (signUpError) throw signUpError;

      if (user) {
        // Sign out immediately after successful registration
        await supabase.auth.signOut();

        toast({
          title: "Pre-registration successful!",
          description: "Thank you for signing up. We'll notify you when we launch!",
        });

        navigate("/");
      }
    } catch (error: any) {
      console.error("Signup error:", error);
      
      if (error.message.includes("User already registered")) {
        toast({
          variant: "destructive",
          title: "Already pre-registered",
          description: "This email is already on our waitlist. You'll receive an email when we launch.",
        });
      } else {
        toast({
          variant: "destructive",
          title: "Error signing up",
          description: error.message,
        });
      }
    }

    setLoading(false);
  };

  return (
    <form onSubmit={handleSignUp} className="mt-8 space-y-6">
      <div className="space-y-4">
        <Input
          type="text"
          placeholder="Username"
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          required
          className="bg-flux-black/50 border-white/20 text-white placeholder:text-white/50"
        />
        <Input
          type="email"
          placeholder="Email address"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          className="bg-flux-black/50 border-white/20 text-white placeholder:text-white/50"
        />
        <Input
          type="password"
          placeholder="Password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
          className="bg-flux-black/50 border-white/20 text-white placeholder:text-white/50"
        />
        <div className="flex items-center space-x-2">
          <Checkbox 
            id="terms" 
            checked={acceptedTerms}
            onCheckedChange={(checked) => setAcceptedTerms(checked as boolean)}
            className="border-white/20 data-[state=checked]:bg-white data-[state=checked]:text-black"
          />
          <label 
            htmlFor="terms" 
            className="text-sm text-white/70 cursor-pointer"
          >
            I agree to the{" "}
            <Link 
              to="/terms" 
              className="text-white hover:text-white/80 underline"
              target="_blank"
            >
              Terms of Service
            </Link>{" "}
            and{" "}
            <Link 
              to="/privacy" 
              className="text-white hover:text-white/80 underline"
              target="_blank"
            >
              Privacy Policy
            </Link>
          </label>
        </div>
      </div>

      <Button
        type="submit"
        className="w-full bg-black hover:bg-white text-white hover:text-black border border-white transition-colors duration-300"
        disabled={loading}
      >
        {loading ? "Registering..." : "Continue"}
      </Button>
    </form>
  );
};